/**
 * Card catalog + suit metadata for the marketing pages. Pure (no DOM), so the prerender can
 * import it in Node. Card objects follow the `Card` shape in API.md §2.
 */
import catalog from '../data/cards.json';

export const SITE = {
  origin: 'https://fairplaydeck.com',
  name: 'Fair Play Deck',
  bookUrl: 'https://www.penguinrandomhouse.com/books/605905/fair-play-by-eve-rodsky/',
  deckUrl: 'https://www.penguinrandomhouse.com/books/647409/the-fair-play-deck-by-eve-rodsky/',
  eveUrl: 'https://www.everodsky.com',
  instituteUrl: 'https://www.fairplaypolicy.org',
  cardsUrl: 'https://fairplaypolicy.org/the-cards',
  startPath: '/start',
};

/** @typedef {{ id: string, slug: string, suit: string, name: string, fullName: string, dailyGrind: boolean, happinessTrio: boolean, perPlayer: boolean, playerSlot: number | null, pairKey: string | null, kids: boolean, definition: string | null, conception: string | null, planning: {t: string, text: string}[], execution: {t: string, text: string}[], msc: string | null, resources: {title: string, url: string}[] }} Card */

/** @type {Card[]} */
export const CARDS = catalog.cards;

/** Suit copy, in deck order. `blurb` paraphrases the book's suit introductions. */
export const SUITS = [
  {
    id: 'home',
    name: 'Home',
    count: 22,
    blurb:
      'Running the physical household. Someone is supposed to know when you’re almost out of toilet paper.',
  },
  {
    id: 'out',
    name: 'Out',
    count: 22,
    blurb:
      'Everything that makes life outside the house possible: the calendar, bills, driving, travel and school breaks.',
  },
  {
    id: 'caregiving',
    name: 'Caregiving',
    count: 22,
    blurb:
      'The needs of kids, family, friends and yourselves. Much of this can’t be outsourced the way Home tasks can.',
  },
  {
    id: 'magic',
    name: 'Magic',
    count: 22,
    blurb:
      'Connection and memory-making: holidays, gifts, romance, magical beings. Expect to re-deal these often.',
  },
  {
    id: 'wild',
    name: 'Wild',
    count: 10,
    blurb:
      'Life events that rock your world, joyful ones included. Playing one starts a re-deal conversation.',
  },
  {
    id: 'unicorn',
    name: 'Unicorn Space',
    count: 2,
    blurb:
      'One per partner. Protected time for the passion that makes you, you. The whole point of the game.',
  },
];

/** @param {string} id */
export const suitOf = (id) => SUITS.find((s) => s.id === id) ?? SUITS[0];

/** @param {string} slug */
export const cardBySlug = (slug) => CARDS.find((c) => c.slug === slug);

/** @param {string} suit */
export const cardsInSuit = (suit) => CARDS.filter((c) => c.suit === suit);

export const DAILY_GRIND_COUNT = CARDS.filter((c) => c.dailyGrind).length;

/** Cards named inside another card's definition, e.g. “Transportation (Kids)”. */
/** @param {Card} card */
function mentioned(card) {
  const text = `${card.definition ?? ''} ${card.execution.map((b) => b.text).join(' ')}`;
  const out = [];
  for (const m of text.matchAll(/[“"]([^”"]{3,60})[”"]/g)) {
    const q = m[1].replace(/[.,]$/, '').toLowerCase();
    const hit = CARDS.find(
      (c) =>
        c.slug !== card.slug &&
        (c.fullName.toLowerCase() === q ||
          c.name.toLowerCase() === q ||
          c.name.toLowerCase().startsWith(`${q} (`)),
    );
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out;
}

/**
 * Related cards: explicit mentions first, then the per-player twin, then neighbours in the
 * same suit (Daily Grinds next to Daily Grinds).
 * @param {Card} card
 * @param {number} [n]
 */
export function relatedCards(card, n = 6) {
  const out = mentioned(card);
  const push = (c) => {
    if (c && c.slug !== card.slug && !out.includes(c)) out.push(c);
  };
  if (card.pairKey) {
    for (const c of CARDS) if (c.pairKey === card.pairKey) push(c);
  }
  const suit = cardsInSuit(card.suit);
  const i = suit.findIndex((c) => c.slug === card.slug);
  const ordered = [...suit.slice(i + 1), ...suit.slice(0, i)];
  for (const c of ordered.filter((c) => c.dailyGrind === card.dailyGrind)) push(c);
  for (const c of ordered) push(c);
  return out.slice(0, n);
}

/** Hero cards shown in the static fan and passed to mountHeroDeck. */
export const HERO_SLUGS = [
  'calendar-keeper',
  'meals-weekday-dinner',
  'unicorn-space-player-1',
  'bedtime-routine-kids',
  'holidays',
];
export const heroCards = () => HERO_SLUGS.map(cardBySlug).filter(Boolean);

/** The four rules (chapter titles of Fair Play, with our one-line gloss). */
export const RULES = [
  {
    n: 'I',
    title: 'All time is created equal',
    body: 'An hour of your time is worth the same as an hour of mine. Nobody’s time is the household’s default.',
  },
  {
    n: 'II',
    title: 'Reclaim your right to be interesting',
    body: 'Each of you deserves time for the passions that make you you, beyond partner, parent and worker.',
  },
  {
    n: 'III',
    title: 'Start where you are now',
    body: 'Begin with an honest look at who does what today. Only one person has to start the change.',
  },
  {
    n: 'IV',
    title: 'Establish your values and standards',
    body: 'Decide which cards matter to your family and what “done” looks like for each one you keep.',
  },
];
